/**
 * useWasteAssociationAnalysis - Hooks for Waste association analysis MVP
 *
 * @remarks
 * Provides data fetching hooks for the simplified Waste association
 * analysis endpoints:
 * - Problems: entities with association issues
 * - Coverage map: edges colored by distance to containers
 * - Gaps: underserved areas
 * - Suggestions: recommended container locations
 *
 * @see WASTE_ASSOCIATIONS_EXPERIENCE.md
 */

import { useState, useEffect, useCallback } from 'react';
import {
  wasteAssociationApi,
  type ProblemsResponse,
  type CoverageMapResponse,
  type GapsResponse,
  type SuggestionsResponse,
  type EntitiesWithStatesResponse,
  type SimpleAssociationState,
  type CoverageColor,
} from '../services/point-association-api.service';

// ══════════════════════════════════════════════════════════════
// useWasteAssociationProblems
// ══════════════════════════════════════════════════════════════

interface UseWasteProblemsOptions {
  /** If false, doesn't load automatically */
  enabled?: boolean;
}

interface UseWasteProblemsReturn {
  /** Problems data */
  data: ProblemsResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error if occurred */
  error: Error | null;
  /** Refetch data */
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching Waste association problems
 */
export function useWasteAssociationProblems(
  projectId: string | null | undefined,
  options: UseWasteProblemsOptions = {},
): UseWasteProblemsReturn {
  const { enabled = true } = options;

  const [data, setData] = useState<ProblemsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!projectId || !enabled) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await wasteAssociationApi.getProblems(projectId);
      setData(result);
    } catch (err) {
      console.error('[useWasteAssociationProblems] Error:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch problems'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}

// ══════════════════════════════════════════════════════════════
// useWasteCoverageMap
// ══════════════════════════════════════════════════════════════

interface UseCoverageMapOptions {
  /** If false, doesn't load automatically */
  enabled?: boolean;
}

interface UseCoverageMapReturn {
  /** Coverage map data */
  data: CoverageMapResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error if occurred */
  error: Error | null;
  /** Refetch data */
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching Waste coverage map
 */
export function useWasteCoverageMap(
  projectId: string | null | undefined,
  options: UseCoverageMapOptions = {},
): UseCoverageMapReturn {
  const { enabled = true } = options;

  const [data, setData] = useState<CoverageMapResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!projectId || !enabled) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await wasteAssociationApi.getCoverageMap(projectId);
      setData(result);
    } catch (err) {
      console.error('[useWasteCoverageMap] Error:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch coverage map'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}

// ══════════════════════════════════════════════════════════════
// useWasteGaps
// ══════════════════════════════════════════════════════════════

interface UseGapsOptions {
  /** If false, doesn't load automatically */
  enabled?: boolean;
}

interface UseGapsReturn {
  /** Gaps data */
  data: GapsResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error if occurred */
  error: Error | null;
  /** Refetch data */
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching Waste coverage gaps
 */
export function useWasteGaps(
  projectId: string | null | undefined,
  options: UseGapsOptions = {},
): UseGapsReturn {
  const { enabled = true } = options;

  const [data, setData] = useState<GapsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!projectId || !enabled) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await wasteAssociationApi.getGaps(projectId);
      setData(result);
    } catch (err) {
      console.error('[useWasteGaps] Error:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch gaps'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}

// ══════════════════════════════════════════════════════════════
// useWasteSuggestions
// ══════════════════════════════════════════════════════════════

interface UseSuggestionsOptions {
  /** If false, doesn't load automatically */
  enabled?: boolean;
}

interface UseSuggestionsReturn {
  /** Suggestions data */
  data: SuggestionsResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error if occurred */
  error: Error | null;
  /** Refetch data */
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching Waste location suggestions
 */
export function useWasteSuggestions(
  projectId: string | null | undefined,
  options: UseSuggestionsOptions = {},
): UseSuggestionsReturn {
  const { enabled = true } = options;

  const [data, setData] = useState<SuggestionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!projectId || !enabled) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await wasteAssociationApi.getSuggestions(projectId);
      setData(result);
    } catch (err) {
      console.error('[useWasteSuggestions] Error:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch suggestions'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}

// ══════════════════════════════════════════════════════════════
// useWasteEntitiesWithStates
// ══════════════════════════════════════════════════════════════

interface UseEntitiesWithStatesOptions {
  /** If false, doesn't load automatically */
  enabled?: boolean;
}

interface UseEntitiesWithStatesReturn {
  /** Entities data */
  data: EntitiesWithStatesResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error if occurred */
  error: Error | null;
  /** Refetch data */
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching Waste entities with simple states for map visualization
 */
export function useWasteEntitiesWithStates(
  projectId: string | null | undefined,
  options: UseEntitiesWithStatesOptions = {},
): UseEntitiesWithStatesReturn {
  const { enabled = true } = options;

  const [data, setData] = useState<EntitiesWithStatesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!projectId || !enabled) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await wasteAssociationApi.getEntitiesWithStates(projectId);
      setData(result);
    } catch (err) {
      console.error('[useWasteEntitiesWithStates] Error:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch entities'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
  };
}

// ══════════════════════════════════════════════════════════════
// REVIEW MODE ACTIONS
// ══════════════════════════════════════════════════════════════

/**
 * Confirm an association (sets manual_lock + valid status)
 */
export async function confirmAssociation(
  entityType: string,
  entityId: string,
): Promise<{ success: boolean; message: string }> {
  return wasteAssociationApi.confirmAssociation(entityType, entityId);
}

/**
 * Ignore an association (sets manual_lock without recalculating)
 */
export async function ignoreAssociation(
  entityType: string,
  entityId: string,
): Promise<{ success: boolean; message: string }> {
  return wasteAssociationApi.ignoreAssociation(entityType, entityId);
}

// ══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ══════════════════════════════════════════════════════════════

/**
 * Get CSS color for simple state
 */
export function getStateColor(state: SimpleAssociationState): string {
  switch (state) {
    case 'OK':
      return '#22c55e'; // green-500
    case 'REVISAR':
      return '#eab308'; // yellow-500
    case 'ERROR':
      return '#ef4444'; // red-500
    default:
      return '#6b7280'; // gray-500
  }
}

/**
 * Get CSS color for coverage
 */
export function getCoverageColor(color: CoverageColor): string {
  switch (color) {
    case 'verde':
      return '#22c55e'; // green-500
    case 'amarillo':
      return '#eab308'; // yellow-500
    case 'rojo':
      return '#ef4444'; // red-500
    default:
      return '#6b7280'; // gray-500
  }
}

/**
 * Get label for simple state
 */
export function getStateLabel(state: SimpleAssociationState): string {
  switch (state) {
    case 'OK':
      return 'Correcto';
    case 'REVISAR':
      return 'Revisar';
    case 'ERROR':
      return 'Error';
    default:
      return 'Desconocido';
  }
}

// Re-export types for convenience
export type {
  ProblemsResponse,
  CoverageMapResponse,
  GapsResponse,
  SuggestionsResponse,
  EntitiesWithStatesResponse,
  SimpleAssociationState,
  CoverageColor,
};
